/**
 * DraftRestoreBanner — inline banner shown at the top of a form when a
 * saved draft is available for restoration.  Consumes the `draft` +
 * `onRestore` + `onDiscard` returned by `useDraft` / `useRestoreDraft`.
 *
 * Two visual variants:
 *   - Amber (default) — same-build draft, safe to restore
 *   - Red (buildIdMismatch=true) — draft written under a different build;
 *     schema may have changed between builds, restore at your own risk
 *     (panel finding: this is the class of bug this feature exists to
 *     protect against — a mid-session deploy is exactly the trigger)
 *
 * Restoring shows an implicit confirm (user chose to restore).  Discard
 * clears the draft immediately.  If neither is clicked, the draft
 * persists for 24h — user can decide later.
 *
 * Window provenance is shown as a suffix rather than a third variant.  The
 * paths using this banner already require a click, so a cross-window draft
 * needs no extra gate here — only honesty about where it came from.  Tone
 * stays driven by buildIdMismatch: schema drift is the stronger signal and
 * must not be outranked by provenance.
 */
import { isForeignWindowDraft } from '../lib/draftStorage'

export default function DraftRestoreBanner ({ draft, onRestore, onDiscard }) {
  if (!draft) return null

  const mismatch = !!draft.buildIdMismatch
  // False whenever either side's window id is the 'unknown' sentinel — see
  // isForeignWindowDraft.  The banner must not claim a provenance it cannot
  // actually establish.
  const foreign = isForeignWindowDraft(draft)
  const savedAt = typeof draft.savedAt === 'number' ? draft.savedAt : Date.now()
  const minsAgo = Math.max(1, Math.round((Date.now() - savedAt) / 60000))
  const timeLabel = minsAgo < 60
    ? `${minsAgo} min ago`
    : minsAgo < 60 * 24
    ? `${Math.round(minsAgo / 60)} hr ago`
    : `${Math.round(minsAgo / (60 * 24))} day ago`

  const tone = mismatch
    ? {
        wrap: 'bg-red-50 border-red-200',
        titleClass: 'text-red-900',
        body: 'text-red-700',
        primary: 'bg-red-600 hover:bg-red-700',
        ghost: 'text-red-700 hover:bg-red-100',
        icon: '⚠️'
      }
    : {
        wrap: 'bg-amber-50 border-amber-200',
        titleClass: 'text-amber-900',
        body: 'text-amber-700',
        primary: 'bg-amber-600 hover:bg-amber-700',
        ghost: 'text-amber-700 hover:bg-amber-100',
        icon: '💾'
      }

  return (
    <div className={`${tone.wrap} border rounded-lg p-3 mb-3 text-sm flex items-start sm:items-center flex-col sm:flex-row justify-between gap-3`}>
      <div className="flex-1">
        <span className={`font-medium ${tone.titleClass}`}>
          {tone.icon} Unsaved changes from {timeLabel}
        </span>
        <span className={`ml-2 ${tone.body}`}>
          {mismatch
            ? 'App was updated since then — restore with care, some fields may have changed.'
            : 'The app reloaded while you were editing.'}
          {foreign && ' (saved in another window)'}
        </span>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onRestore}
          className={`${tone.primary} text-white text-xs font-medium rounded px-3 py-1.5 transition-colors`}
        >
          Restore
        </button>
        <button
          type="button"
          onClick={onDiscard}
          className={`${tone.ghost} text-xs font-medium rounded px-3 py-1.5 transition-colors`}
        >
          Discard
        </button>
      </div>
    </div>
  )
}
